import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Grid } from '@nike/nike-design-system-components';
import { nike } from '@nike/nike-design-tokens';
const { podiumCdsColorBgSecondary, podiumCdsColorFocusRing } = nike;
import { NAV_MAIN_SECTION_HEIGHT, DESKTOP_TOP_HEIGHT } from '../../constants';
import { mobileOnly, desktopOnly } from '../../utils/sharedStyles';
import { SEARCH_ICON } from '../VisualSearch/styles';

const exposedSearchHeight = 56;

export const swooshSvg = 'swoosh-svg';
export const swooshLink = 'swoosh-link';
export const swoosh = 'swoosh';
export const exposedSearch = 'exposed-search';
export const exposedSearchHidden = 'exposed-search-hidden';
export const noPeekaboo = 'no-peekaboo';
export const noAnimation = 'no-animation';

const baseFixed = css`
  position: fixed;
  background: var(--podium-cds-color-bg-primary);
  width: 100%;
`;

export const StyledHeader = styled.header`
  position: relative;
  z-index: 2;
  background-color: var(--podium-cds-color-bg-primary);
  transition: transform 150ms ease;
  &.is-fixed {
    ${baseFixed}
    top: 0;
    left: 0;
  }
  &.is-hidden {
    transform: translateY(-${NAV_MAIN_SECTION_HEIGHT}px);
  }
  &.${exposedSearchHidden} {
    transform: translateY(-${NAV_MAIN_SECTION_HEIGHT + exposedSearchHeight}px);
  }

  &.${noAnimation} {
    transition: none;
  }
`;

export const StyledDiv = styled.div`
  position: relative;
`;

export const shoppingMenuGrid = 'shopping-menu-grid';
export const StyledGrid = styled(Grid)`
  .${shoppingMenuGrid} {
    height: 60px;
  }
`;

export const StyledNav = styled.nav`
  margin: auto;
  height: ${NAV_MAIN_SECTION_HEIGHT}px;
  &.${exposedSearch} {
    height: ${NAV_MAIN_SECTION_HEIGHT + exposedSearchHeight}px;
  }

  @media (${desktopOnly}) {
    height: calc(${DESKTOP_TOP_HEIGHT}px + ${NAV_MAIN_SECTION_HEIGHT}px);
    &.${noPeekaboo}.topbar-hidden {
      transform: translateY(-${DESKTOP_TOP_HEIGHT}px);
    }
  }

  &.${noPeekaboo} {
    ${baseFixed}
    transition: transform 150ms ease;
    z-index: 3;
  }

  .${swoosh} {
    display: inline-flex;
    height: 60px;
  }
  .${swooshSvg} {
    // ensures fill is using the color provided by the style sheet for dark mode
    path {
      fill: currentColor;
    }
    transform: scale(1.33);
    height: 100%;
    width: 100%;
  }
  .${swooshLink} {
    z-index: 1;
    display: inline-block;
    height: 59px;
    width: 59px;
    margin: 1px 0;
  }
`;

export const FixedSpacer = styled.div`
  @media (${desktopOnly}) {
    height: ${DESKTOP_TOP_HEIGHT + NAV_MAIN_SECTION_HEIGHT}px;
  }

  width: 100%;
  display: block;
  height: ${NAV_MAIN_SECTION_HEIGHT}px;
`;
/**
 * ExposedSearch is a StyledComponent wrapper for the VisualSearch component
 * that applies styling to the Search bar to make it full width on mobile.
 * Currently only applicable on nike.com/cart.
 * @returns {import('@emotion/styled').StyledComponent} Styled component for the ExposedSearch container.
 */
export const ExposedSearch = styled.div`
  @media (${mobileOnly}) {
    display: flex;
    width: 100%;
    height: 40px;
    padding: 0 24px;
    margin-bottom: 16px;
    [data-testid='visual-search-container'] {
      width: 100%;
      height: 40px;
      .gn-search-bar {
        &.search-opened {
          top: 0;
          padding-top: 12px;

          .bar-search-results-tray {
            min-height: 80vh;
          }
        }
        &:not(.search-opened) {
          grid-template-columns: 0 100% 0;
          .bar-input-container {
            > div {
              width: 100%;
              .search-input-container {
                // This is where the button, which appears to be a input, starts.
                display: block;
                width: 100%;
                height: 40px;
                input[type='search'] {
                  // We don't need the input, because there's a dialog that handles search.
                  display: none;
                }
                .search-start-icon-container {
                  // Button Container
                  width: 100%;
                  #${SEARCH_ICON} {
                    justify-content: flex-start;
                    align-items: center;
                    background-color: ${podiumCdsColorBgSecondary};
                    width: 100%;
                    color: var(--podium-cds-color-text-secondary-inverse);
                    svg {
                      color: var(--podium-cds-color-text-primary);
                      margin-top: 3px;
                      margin-right: 7px;
                    }
                    &::before {
                      border-color: transparent;
                    }
                    &::after {
                      // This is the label that is added to the search icon button
                      content: '${props => props.searchLabel}';
                    }
                    &:hover {
                      background-color: var(--podium-cds-color-bg-hover);
                      border-color: var(--podium-cds-color-bg-hover);
                    }
                    &:focus-visible {
                      box-shadow: 0 0 0 2px ${podiumCdsColorFocusRing};
                    }
                  }
                }
                .search-end-icon-container {
                  display: none;
                }
              }
              .cancel-slot-container {
                display: none;
              }
            }
          }
        }
      }
    }
  }
`;
